import React from 'react';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { Building2, Globe, Mail, User, Phone, DollarSign, Calendar, Film, ExternalLink, Check, Tag, Loader2, X, Info } from 'lucide-react';
import type { CreativeSlotState, PublisherConfig, SelfServiceFormData } from '../../../types/selfService';
import {
  buildChargeSchedule,
  chargeScheduleTotal,
  calendarMonthsSpanned,
  computePlaysBoost,
  playsBoostWinner,
  selectPlaysBoostSources,
} from '../../../types/selfService';
import { getTargetingRow } from '../targetingRow';
import ImpressionsCalculator from '../shared/ImpressionsCalculator';
import { formatMoneySmart } from '../shared/format';
import { useCreativePreviewUrl } from '../shared/useCreativePreviewUrl';
import { FieldCard, SectionHeading, StepHeader, accentVars, tintFromAccent } from './_shared';
import { displayStepNumber } from '../wizardSteps';
import type { CodeValidation } from '../useCodeValidation';

interface ReviewPayStepProps {
  config: PublisherConfig;
  codeValidation: CodeValidation;
  // Full creative state for the commercial summary. Each slot is rendered
  // with its own thumbnail (resolved via /creatives/preview-url) and date /
  // weight chips when there are multiple commercials.
  slots: CreativeSlotState[];
  slug: string;
  // CTV-471: when the signed-in buyer skips the Verify step, this step
  // renumbers from 5 to 4 so the header matches the progress bar.
  skipVerify: boolean;
}

const formatDateShort = (s: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

const formatDate = (dateStr: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
};

const ReviewPayStep: React.FC<ReviewPayStepProps> = ({ config, codeValidation, slots, slug, skipVerify }) => {
  const accentColor = config.accentColor || config.primaryColor;
  const { getValues, control, setValue } = useFormContext<SelfServiceFormData>();
  const values = getValues();
  // CTV-640: every field the boost and money math reads is WATCHED (a
  // getValues() snapshot goes stale when the buyer edits a code or budget on
  // this very step). `values` stays for the static identity rows only.
  const watchedPaymentPlan = useWatch({ control, name: 'paymentPlan' });
  const watchedStartDate = useWatch({ control, name: 'startDate' });
  const watchedEndDate = useWatch({ control, name: 'endDate' });
  const watchedBudget = useWatch({ control, name: 'budget' });
  const watchedSelectedProduct = useWatch({ control, name: 'selectedProduct' });
  const watchedPromoCode = useWatch({ control, name: 'promoCode' });

  // The PaymentPlanSection toggle is hidden when calendarMonthsSpanned < 2,
  // but the form value can carry a stale 'monthly' selection from when the
  // window was longer. Reset it to 'upfront' so the persisted submission row
  // (and downstream Stripe wiring) doesn't end up with payment_plan='monthly'
  // for a campaign that only spans one calendar month.
  React.useEffect(() => {
    const months = calendarMonthsSpanned(watchedStartDate, watchedEndDate);
    if (months < 2 && watchedPaymentPlan !== 'upfront') {
      setValue('paymentPlan', 'upfront', { shouldDirty: true });
    }
  }, [watchedStartDate, watchedEndDate, watchedPaymentPlan, setValue]);

  // CTV-558: targeting row derives from the shared helper so it reflects the
  // active geo sub-mode (states / DMAs / radius) instead of a generic
  // "Custom geotargeting" label.
  const watchedTargetingMode = useWatch({ control, name: 'targetingMode' });
  const watchedGeoMode = useWatch({ control, name: 'geoMode' });
  const watchedSelectedStates = useWatch({ control, name: 'selectedStates' });
  const watchedSelectedDMAs = useWatch({ control, name: 'selectedDMAs' });
  const watchedGeoTargetCenter = useWatch({ control, name: 'geoTargetCenter' });
  const watchedGeoTargetRadiusMiles = useWatch({ control, name: 'geoTargetRadiusMiles' });
  const watchedGeoTargetZips = useWatch({ control, name: 'geoTargetZips' });
  const watchedTargetMarkets = useWatch({ control, name: 'targetMarkets' });
  const targetingRow = getTargetingRow(
    {
      targetMarkets: watchedTargetMarkets ?? values.targetMarkets,
      targetingMode: watchedTargetingMode,
      geoMode: watchedGeoMode,
      selectedStates: watchedSelectedStates,
      selectedDMAs: watchedSelectedDMAs,
      geoTargetCenter: watchedGeoTargetCenter,
      geoTargetRadiusMiles: watchedGeoTargetRadiusMiles,
      geoTargetZips: watchedGeoTargetZips,
    },
    config,
  );

  return (
    <div className="-m-4 sm:-m-6 md:-m-8" style={accentVars(accentColor, config.primaryColor)}>
      <div className="px-6 sm:px-10 md:px-14 pt-6 pb-6">
        <StepHeader step={displayStepNumber(5, skipVerify)} label="Review & pay" accentColor={accentColor} />
      </div>

      <div className="px-6 sm:px-10 md:px-14 pb-10 space-y-5">
        {/* Business */}
        <SectionCard title="Business" accentColor={accentColor}>
          <Row icon={Building2} label="Business" value={values.businessName} accentColor={accentColor} />
          {values.businessWebsite && <Row icon={Globe} label="Website" value={values.businessWebsite} accentColor={accentColor} />}
          <Row icon={Mail} label="Email" value={values.contactEmail} accentColor={accentColor} />
          {values.contactName && <Row icon={User} label="Contact" value={values.contactName} accentColor={accentColor} />}
          {values.contactPhone && <Row icon={Phone} label="Phone" value={values.contactPhone} accentColor={accentColor} />}
        </SectionCard>

        {/* Commercial */}
        <SectionCard title="Commercial" accentColor={accentColor}>
          {(() => {
            const slotHasContent = (s: CreativeSlotState) => !!s.storagePath || !!s.filename;
            const visible: CreativeSlotState[] = slots.filter(slotHasContent);
            if (visible.length === 0) {
              return (
                <Row icon={Film} label="Video" value="No file uploaded" accentColor={accentColor} />
              );
            }
            const multi = visible.length >= 2;
            return visible.map((slot) => (
              <CommercialSummaryRow
                key={slot.slotId}
                slot={slot}
                slug={slug}
                accentColor={accentColor}
                showWeight={multi}
                showDates={multi}
              />
            ));
          })()}
        </SectionCard>

        {/* Campaign */}
        <SectionCard title="Campaign" accentColor={accentColor}>
          {values.campaignName && <Row icon={Tag} label="Name" value={values.campaignName} accentColor={accentColor} />}
          <Row icon={DollarSign} label="Budget" value={`$${formatMoneySmart(values.budget)}`} accentColor={accentColor} />
          <Row icon={Calendar} label="Start" value={formatDate(values.startDate)} accentColor={accentColor} />
          <Row icon={Calendar} label="End" value={formatDate(values.endDate)} accentColor={accentColor} />
          <Row icon={targetingRow.icon} label={targetingRow.label} value={targetingRow.value} accentColor={accentColor} />
        </SectionCard>

        {/* Itemized play boosts active for the selected plan. Code-side
            (promo OR referral, bigger-of) plus prepay (upfront only) — each
            shown as its own row in the impressions card so the buyer can
            see exactly what each bonus is contributing. CTV-640: all boost
            math flows through selectPlaysBoostSources / computePlaysBoost,
            the same integer-point formulas the server books. */}
        {(() => {
          const sources = selectPlaysBoostSources({
            promoPercent: codeValidation.promo.state === 'valid' ? codeValidation.promo.discountPercent : null,
            promoLabel: codeValidation.promo.state === 'valid'
              ? `Promo — ${(watchedPromoCode || '').toUpperCase()}`
              : null,
            referralPercent: codeValidation.ref.state === 'valid' ? codeValidation.ref.defaultDiscountPercent : null,
            referralLabel: codeValidation.ref.state === 'valid'
              ? `Referral — ${codeValidation.ref.name}`
              : null,
            // This card summarizes the SELECTED plan, so the prepay bonus
            // counts only once the buyer picked upfront. The comparison
            // cards in PaymentPlanSection deliberately gate differently —
            // see includePrepayBonus there.
            includePrepayBonus: watchedPaymentPlan === 'upfront',
            startDate: watchedStartDate,
            endDate: watchedEndDate,
          });

          // CTV-575: on the monthly plan the "Total cost" must equal the sum
          // of the per-installment charges shown in the schedule below. Pass
          // that sum as the override (null for upfront, or when the schedule
          // can't be built). Under CTV-635 both plans total the budget, but
          // the override keeps the card equal to the schedule's rounding.
          const monthlySchedule =
            watchedPaymentPlan === 'monthly'
              ? buildChargeSchedule(watchedStartDate, watchedEndDate, watchedBudget ?? 0)
              : [];
          const totalOverride = monthlySchedule.length ? chargeScheduleTotal(monthlySchedule) : null;

          return (
            <>
              <ImpressionsCalculator
                budget={watchedBudget}
                cpm={config.products.find((p) => p.id === watchedSelectedProduct)?.cpm ?? 0}
                accentColor={accentColor}
                boostSources={sources}
                totalOverride={totalOverride}
              />

              <PaymentPlanSection config={config} accentColor={accentColor} validation={codeValidation} />
            </>
          );
        })()}

        {/* Promo & referral — manual entry + validation feedback. The form
            fields are hydrated from URL capture / localStorage in the wizard. */}
        <PromoReferralSection accentColor={accentColor} validation={codeValidation} />

        {/* CTV-639: the terms + refund checkboxes were removed — legal
            acceptance now happens via the CTVBuyer Advertiser Terms gate on
            step 1. This stays as a plain reminder about creative review. */}
        <p className="text-[13px] text-slate-600 leading-relaxed">
          I understand my commercial will be reviewed before going live.
        </p>
      </div>
    </div>
  );
};

// Manual entry + validation feedback for referral and promo codes. Inputs
// stay always-visible (rather than hidden behind a "Have a code?" toggle)
// so an applied code from URL capture is obvious and revocable.
const PromoReferralSection: React.FC<{ accentColor: string; validation: CodeValidation }> = ({ accentColor, validation }) => {
  const { control, setValue } = useFormContext<SelfServiceFormData>();

  return (
    <div>
      <SectionHeading>Promo &amp; referral</SectionHeading>
      <FieldCard accentColor={accentColor}>
        <Controller
          name="promoCode"
          control={control}
          render={({ field }) => (
            <CodeRow
              label="Promo code"
              placeholder="e.g. MEMDAY10"
              value={field.value || ''}
              onChange={(v) => field.onChange(v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 32))}
              onClear={() => setValue('promoCode', '', { shouldDirty: true })}
              accentColor={accentColor}
              status={renderPromoStatus(validation, accentColor)}
            />
          )}
        />
        <Controller
          name="referralCode"
          control={control}
          render={({ field }) => (
            <CodeRow
              label="Referral code"
              placeholder="e.g. JANDOE"
              value={field.value || ''}
              onChange={(v) => field.onChange(v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))}
              onClear={() => setValue('referralCode', '', { shouldDirty: true })}
              accentColor={accentColor}
              status={renderRefStatus(validation, accentColor)}
            />
          )}
        />
      </FieldCard>
    </div>
  );
};

const CodeRow: React.FC<{
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  onClear: () => void;
  accentColor: string;
  status: React.ReactNode;
}> = ({ label, placeholder, value, onChange, onClear, accentColor, status }) => (
  <div className="px-5 py-4">
    <div className="text-[10px] font-medium tracking-[0.08em] uppercase text-slate-500 mb-1.5">{label}</div>
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent border-0 px-0 py-1 text-[15px] text-slate-900 placeholder-slate-300 caret-[color:var(--accent)] focus:outline-none focus:ring-0 font-mono tracking-widest"
        style={{ caretColor: accentColor }}
      />
      {value && (
        <button
          type="button"
          onClick={onClear}
          className="p-1 text-slate-400 hover:text-slate-700"
          aria-label="Clear"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
    {status && <div className="mt-1.5 text-[12px] leading-relaxed">{status}</div>}
  </div>
);

// When both a referral and a promo validate, only the bigger of the two
// actually applies to the play count (selectPlaysBoostSources' max rule).
// Each render function takes the full CodeValidation so it can mute its own
// pill when the other code is strictly larger and would supersede it.
// CTV-640: the supersede state comes from playsBoostWinner, which uses
// strict > on both sides so ties keep both pills active — they each "apply"
// the same percentage, so neither is misleading. (The winner LABEL in the
// calculator uses >= to pick exactly one; the asymmetry is deliberate.)
function renderRefStatus(validation: CodeValidation, accentColor: string): React.ReactNode {
  const ref = validation.ref;
  switch (ref.state) {
    case 'empty': return null;
    case 'pending':
      return (
        <span className="inline-flex items-center gap-1.5 text-slate-400">
          <Loader2 className="w-3 h-3 animate-spin" /> Checking…
        </span>
      );
    case 'valid': {
      // A referral without a defaultDiscountPercent is just attribution and
      // isn't competing with the promo on bonus plays — playsBoostWinner
      // treats null as "no bonus to lose".
      const { referralSupersededByPromo: supersededByPromo } = playsBoostWinner(
        validation.promo.state === 'valid' ? validation.promo.discountPercent : 0,
        ref.defaultDiscountPercent,
      );
      return (
        <span
          className="inline-flex items-center gap-1.5"
          style={{ color: supersededByPromo ? 'rgb(100, 116, 139)' : accentColor }}
        >
          <Check className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            Referred by <strong className="font-semibold">{ref.name}</strong>
            {ref.defaultDiscountPercent != null && (
              supersededByPromo ? (
                <> — promo's larger bonus applies instead</>
              ) : (
                <> — <strong className="font-semibold">+{ref.defaultDiscountPercent}% plays</strong> applied</>
              )
            )}
          </span>
        </span>
      );
    }
    case 'invalid':
      return <span className="text-red-600">Code not recognized.</span>;
  }
}

function renderPromoStatus(validation: CodeValidation, accentColor: string): React.ReactNode {
  const promo = validation.promo;
  switch (promo.state) {
    case 'empty': return null;
    case 'pending':
      return (
        <span className="inline-flex items-center gap-1.5 text-slate-400">
          <Loader2 className="w-3 h-3 animate-spin" /> Checking…
        </span>
      );
    case 'valid': {
      const promoPct = promo.discountPercent;
      const { promoSupersededByReferral: supersededByRef } = playsBoostWinner(
        promoPct,
        validation.ref.state === 'valid' ? validation.ref.defaultDiscountPercent : null,
      );
      return (
        <span
          className="inline-flex items-center gap-1.5"
          style={{ color: supersededByRef ? 'rgb(100, 116, 139)' : accentColor }}
        >
          <Check className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            <strong className="font-semibold">+{promoPct}% plays</strong>
            {supersededByRef ? (
              <> — referral's larger bonus applies instead</>
            ) : (
              <> applied</>
            )}
          </span>
        </span>
      );
    }
    case 'invalid': {
      const msg = promo.reason === 'expired' ? 'This code has expired.'
        : promo.reason === 'exhausted' ? 'This code has reached its usage limit.'
        : promo.reason === 'already_used' ? "You've already used this code."
        : promo.reason === 'inactive' ? 'This code is inactive.'
        : promo.reason === 'rate_limited' ? 'Too many attempts. Wait a minute and try again.'
        : 'Code not recognized.';
      return <span className="text-red-600">{msg}</span>;
    }
  }
}

function ordinalDay(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

const PaymentPlanSection: React.FC<{
  config: PublisherConfig;
  accentColor: string;
  validation: CodeValidation;
}> = ({ config, accentColor, validation }) => {
  const { control, watch } = useFormContext<SelfServiceFormData>();
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const budget = watch('budget') ?? 0;
  const selectedProduct = watch('selectedProduct');
  const months = calendarMonthsSpanned(startDate, endDate);
  if (months < 2) return null;

  const cpm = config.products.find((p) => p.id === selectedProduct)?.cpm ?? 0;
  // CTV-640: this is the plan COMPARISON UI, so each card computes with its
  // own includePrepayBonus literal (true for upfront, false for monthly)
  // regardless of the buyer's current selection — gating on the selection
  // would hide the bonus until the buyer clicked, which is backwards for a
  // merchandising affordance. The summary card above gates on the SELECTED
  // plan instead; the two gates differ on purpose.
  const codeInputs = {
    promoPercent: validation.promo.state === 'valid' ? validation.promo.discountPercent : null,
    referralPercent: validation.ref.state === 'valid' ? validation.ref.defaultDiscountPercent : null,
    startDate,
    endDate,
  };
  const upfrontSources = selectPlaysBoostSources({ ...codeInputs, includePrepayBonus: true });
  const monthlySources = selectPlaysBoostSources({ ...codeInputs, includePrepayBonus: false });
  const upfront = computePlaysBoost(budget, cpm, upfrontSources);
  const monthly = computePlaysBoost(budget, cpm, monthlySources);
  // Integer percentage points for the badge copy.
  const prepayPct = upfrontSources.find((s) => s.key === 'prepay')?.percent ?? 0;
  const codePct = upfrontSources.find((s) => s.key === 'code')?.percent ?? 0;
  const showStack = prepayPct > 0 && codePct > 0;
  const basePlays = upfront.base;
  const upfrontPlays = upfront.total;
  const monthlyPlays = monthly.total;
  const upfrontBonusPlays = upfront.bonus;
  const monthlyBonusPlays = monthly.bonus;

  return (
    <div>
      <style>{`
        @keyframes bonusPop {
          0% { transform: scale(0.4) rotate(-4deg); opacity: 0; }
          60% { transform: scale(1.15) rotate(2deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        .animate-bonus-pop { animation: bonusPop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
      `}</style>
      <SectionHeading>Payment plan</SectionHeading>
      <Controller
        name="paymentPlan"
        control={control}
        render={({ field }) => (
          <>
          <FieldCard accentColor={accentColor}>
            <PlanOption
              selected={field.value === 'upfront'}
              onSelect={() => field.onChange('upfront')}
              accentColor={accentColor}
              title="Pay upfront"
              titleExtra={
                <>
                  {prepayPct > 0 && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.08em] border"
                      style={{
                        backgroundColor: tintFromAccent(accentColor, 0.1),
                        borderColor: tintFromAccent(accentColor, 0.3),
                        color: accentColor,
                      }}
                    >
                      +{prepayPct}% plays
                    </span>
                  )}
                  {showStack && (
                    <span
                      key={`bonus-${codePct}`}
                      className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.08em] bg-emerald-100 text-emerald-800 border border-emerald-300 animate-bonus-pop"
                    >
                      and another +{codePct}%!
                    </span>
                  )}
                </>
              }
              subtitle={
                prepayPct > 0
                  ? `Receive ${prepayPct}% extra streaming ad plays for paying for your full campaign upfront.`
                  : 'Pay your full campaign cost in one charge today.'
              }
              right={
                <div className="text-right leading-tight">
                  {upfrontBonusPlays > 0 && (
                    <div className="text-[11px] tabular-nums text-slate-400 line-through leading-none mb-1">
                      {basePlays.toLocaleString()}
                    </div>
                  )}
                  <div
                    className="text-[14px] font-semibold tracking-tight tabular-nums"
                    style={{ color: upfrontBonusPlays > 0 ? accentColor : 'rgb(15, 23, 42)' }}
                  >
                    {upfrontPlays.toLocaleString()}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.1em] text-slate-400">plays</div>
                </div>
              }
            />
            <PlanOption
              selected={field.value === 'monthly'}
              onSelect={() => field.onChange('monthly')}
              accentColor={accentColor}
              title="Pay month by month"
              titleExtra={monthly.totalPercent > 0 ? (
                <span
                  key={`monthly-bonus-${monthly.totalPercent}`}
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.08em] bg-emerald-100 text-emerald-800 border border-emerald-300 animate-bonus-pop"
                >
                  +{monthly.totalPercent}% plays
                </span>
              ) : undefined}
              subtitle={
                `First payment due on ${formatDate(startDate)}, then billed on the ${ordinalDay(new Date(startDate + 'T00:00:00').getDate())} of each month. Cancel anytime — current month plays out.`
              }
              right={
                <div className="text-right leading-tight">
                  {monthlyBonusPlays > 0 && (
                    <div className="text-[11px] tabular-nums text-slate-400 line-through leading-none mb-1">
                      {basePlays.toLocaleString()}
                    </div>
                  )}
                  <div
                    className="text-[14px] font-semibold tracking-tight tabular-nums"
                    style={{ color: monthlyBonusPlays > 0 ? accentColor : 'rgb(15, 23, 42)' }}
                  >
                    {monthlyPlays.toLocaleString()}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.1em] text-slate-400">plays</div>
                </div>
              }
            />
            {field.value === 'monthly' && (() => {
              // CTV-635: split the BUDGET across anniversary periods; each
              // charge is exactly its period's budget portion (no service-fee
              // surcharge), so "Total over N months" equals the budget.
              const schedule = buildChargeSchedule(startDate, endDate, budget);
              if (schedule.length === 0) return null;
              const scheduleTotal = chargeScheduleTotal(schedule);
              const [first, ...upcoming] = schedule;
              return (
                <div className="mt-3 rounded-xl border border-slate-200 overflow-hidden bg-white">
                  <div
                    className="px-4 py-4 border-b border-slate-200"
                    style={{ backgroundColor: tintFromAccent(accentColor, 0.04) }}
                  >
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <div className="text-[10px] uppercase tracking-[0.1em] font-semibold text-slate-500">
                          First payment due
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {first.label} · {first.coversLabel}
                        </div>
                      </div>
                    <div
                      className="text-[24px] font-bold tracking-tight tabular-nums leading-none"
                      style={{ color: accentColor }}
                    >
                      ${formatMoneySmart(first.amount)}
                    </div>
                  </div>

                  {upcoming.length > 0 && (
                    <div className="px-4 py-3.5">
                      <div className="text-[10px] uppercase tracking-[0.1em] font-semibold text-slate-500 mb-2.5">
                        Upcoming
                      </div>
                      <ul className="divide-y divide-slate-100">
                        {upcoming.map((item, i) => (
                          <li key={i} className="flex items-baseline justify-between gap-3 py-2 text-[13px] first:pt-0 last:pb-0">
                            <div className="min-w-0">
                              <div className="text-slate-700">{item.label}</div>
                              <div className="text-[11px] text-slate-400 truncate">
                                {item.coversLabel}
                                {item.isPartial && (
                                  <> · prorated for {item.days} {item.days === 1 ? 'day' : 'days'}</>
                                )}
                              </div>
                            </div>
                            <span className="tabular-nums font-medium text-slate-700 shrink-0">${formatMoneySmart(item.amount)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="px-4 py-3.5 bg-slate-50 border-t border-slate-200 flex items-baseline justify-between">
                    <span className="text-[13px] text-slate-600 font-medium">
                      Total over {schedule.length} {schedule.length === 1 ? 'month' : 'months'}
                    </span>
                    <span className="text-[18px] tabular-nums font-bold text-slate-900">
                      ${formatMoneySmart(scheduleTotal)}
                    </span>
                  </div>
                </div>
              );
            })()}
          </FieldCard>
          {!field.value && (
            <p className="mt-1.5 inline-flex items-start gap-1.5 text-[12px] font-medium text-slate-500">
              <Info className="w-3.5 h-3.5 mt-[1px] flex-shrink-0" aria-hidden />
              <span>Choose a payment plan to continue</span>
            </p>
          )}
          </>
        )}
      />
    </div>
  );
};

const PlanOption: React.FC<{
  selected: boolean;
  onSelect: () => void;
  accentColor: string;
  title: string;
  titleExtra?: React.ReactNode;
  subtitle: React.ReactNode;
  right: React.ReactNode;
}> = ({ selected, onSelect, accentColor, title, titleExtra, subtitle, right }) => (
  <button
    type="button"
    onClick={onSelect}
    className="w-full flex items-start gap-4 px-5 py-4 text-left transition-colors hover:bg-slate-50"
    style={{ backgroundColor: selected ? tintFromAccent(accentColor, 0.06) : undefined }}
  >
    <div
      className="mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors"
      style={{
        borderColor: selected ? accentColor : 'rgb(203, 213, 225)',
        backgroundColor: selected ? accentColor : 'transparent',
      }}
    >
      {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[14px] font-semibold tracking-tight text-slate-900 flex items-center flex-wrap">
        <span>{title}</span>
        {titleExtra}
      </div>
      <div className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">{subtitle}</div>
    </div>
    <div className="shrink-0 ml-3">{right}</div>
  </button>
);

// Per-commercial summary row used inside the Commercial SectionCard. Renders
// a small 16:9 thumbnail (signed via /creatives/preview-url when storagePath
// is set), the filename + size, optional rotation weight chip and date window
// when more than one commercial is in play, and the slot's click-through URL
// on a secondary line. Falls back to a Film glyph when the preview can't load
// (mid-upload or signed-URL failure).
const CommercialSummaryRow: React.FC<{
  slot: CreativeSlotState;
  slug: string;
  accentColor: string;
  showWeight: boolean;
  showDates: boolean;
}> = ({ slot, slug, accentColor, showWeight, showDates }) => {
  const { url, posterUrl } = useCreativePreviewUrl(slug, slot.storagePath);
  const dateLine = showDates && slot.startDate && slot.endDate
    ? `${formatDateShort(slot.startDate)} – ${formatDateShort(slot.endDate)}`
    : null;
  return (
    <div className="flex items-center gap-3 px-5 py-3">
      <div className="w-20 aspect-video rounded-md overflow-hidden bg-slate-900 shrink-0 relative">
        {posterUrl ? (
          // CTV-508: server-generated poster (representative non-black frame).
          <img
            src={posterUrl}
            alt={slot.filename || 'Creative thumbnail'}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        ) : url ? (
          <video
            src={url}
            muted
            preload="metadata"
            playsInline
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Film className="w-3.5 h-3.5 text-white/70" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-slate-900 font-medium tracking-tight truncate min-w-0">
          {slot.filename || '—'}
        </div>
        {(slot.sizeBytes > 0 ||
          (showWeight && slot.segmentShares.length > 0) ||
          dateLine) && (
          <div className="mt-0.5 flex items-center gap-2 flex-wrap">
            {slot.sizeBytes > 0 && (
              <span className="text-[11px] tabular-nums text-slate-400 shrink-0">
                {(slot.sizeBytes / (1024 * 1024)).toFixed(1)} MB
              </span>
            )}
            {showWeight && slot.segmentShares.length > 0 && (
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded-full shrink-0"
                style={{ backgroundColor: tintFromAccent(accentColor, 0.1), color: accentColor }}
              >
                {slot.segmentShares.map((s) => `${s.share}%`).join(' · ')}
              </span>
            )}
            {dateLine && (
              <span className="text-[11px] text-slate-500 shrink-0">{dateLine}</span>
            )}
          </div>
        )}
        {slot.clickThroughUrl && (
          <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500 min-w-0">
            <ExternalLink className="w-3 h-3 shrink-0" />
            <span className="truncate">{slot.clickThroughUrl}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const SectionCard: React.FC<{ title: string; accentColor: string; children: React.ReactNode }> = ({ title, accentColor, children }) => (
  <div>
    <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400 mb-2 px-1">
      {title}
    </div>
    <FieldCard accentColor={accentColor}>{children}</FieldCard>
  </div>
);

interface RowProps {
  icon: React.FC<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: string;
  accentColor: string;
}

const Row: React.FC<RowProps> = ({ icon: Icon, label, value, accentColor }) => (
  <div className="flex items-center gap-3 px-5 py-3">
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
      style={{ backgroundColor: tintFromAccent(accentColor, 0.08), color: accentColor }}
    >
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className="text-[13px] text-slate-900 font-medium tracking-tight truncate">{value || '—'}</div>
    </div>
  </div>
);

export default ReviewPayStep;
